// DataGridSortableFT.jsx
import React, { useEffect, useState } from "react";

export default function SortableGrid({ rows = [], columns = [] }) {
  const [displayedRows, setDisplayedRows] = useState(rows);
  const [sortDirections, setSortDirections] = useState({}); // { [field]: 'asc'|'desc' }
  const [sortSequence, setSortSequence] = useState([]);     // click order; LAST is HIGHEST priority

  useEffect(() => {
    setDisplayedRows(rows);
    setSortDirections({});
    setSortSequence([]);
  }, [rows]);

  const nextDirection = (dir) =>
    dir === undefined ? "asc" : dir === "asc" ? "desc" : undefined;

  const promoteToEnd = (seq, field) => {
    // remove if present, then append to end (so it becomes highest priority)
    const without = seq.filter((f) => f !== field);
    return [...without, field];
  };

  const handleHeaderClick = (field) => {
    const current = sortDirections[field];
    const next = nextDirection(current);

    setSortDirections((prev) => {
      const copy = { ...prev };
      if (next === undefined) delete copy[field];
      else copy[field] = next;
      return copy;
    });

    setSortSequence((prev) => {
      if (next === undefined) {
        // neutral → remove; do NOT re-sort immediately
        return prev.filter((f) => f !== field);
      }
      // asc/desc → move to END so it becomes highest priority
      return promoteToEnd(prev, field);
    });

    if (next !== undefined) {
      // Projected state after this click
      const projectedSequence = promoteToEnd(sortSequence, field);
      const projectedDirections = { ...sortDirections, [field]: next };

      setDisplayedRows((curr) =>
        multiSort(curr, makeComparator({ sequence: projectedSequence, directions: projectedDirections }, columns))
      );
    }
  };

  const headerIndicator = (field) => {
    const dir = sortDirections[field];
    const idx = sortSequence.indexOf(field);
    const badge = idx >= 0 ? idx + 1 : null; // lower numbers = older; highest priority is last
    return (
      <span className="dgsft-header-indicator" aria-hidden="true">
        {dir === "asc" ? "▲" : dir === "desc" ? "▼" : "∙"}
        {badge && <span className="dgsft-badge">{badge}</span>}
      </span>
    );
  };

  const formatForDisplay = (col, value) => {
    if (value == null) return "";
    switch (col.FIELDTYPE) {
      case "date": {
        const d = coerceDate(value);
        return d ? d.toLocaleString() : String(value);
      }
      case "boolean":
        return value ? "True" : "False";
      default:
        return String(value);
    }
  };

  return (
    <div style={{ "--dgsft-columns": `repeat(${columns.length}, minmax(140px, 1fr))` }}>
        <div className="dgsft-wrap">
            <style>{styles}</style>
            <div className="dgsft-table" role="table" aria-label="Data grid">
                <div className="dgsft-thead" role="rowgroup">
                    <div className="dgsft-row dgsft-header" role="row">
                        {columns.map((col) => (
                        <button
                            key={col.FIELDNAME}
                            type="button"
                            className={`dgsft-cell dgsft-th ${sortDirections[col.FIELDNAME] ? "is-sorted" : ""}`}
                            role="columnheader"
                            aria-sort={
                            sortDirections[col.FIELDNAME] === "asc"
                                ? "ascending"
                                : sortDirections[col.FIELDNAME] === "desc"
                                ? "descending"
                                : "none"
                            }
                            onClick={() => handleHeaderClick(col.FIELDNAME)}
                            title="Click to change sort (neutral → asc → desc → neutral)"
                        >
                            <span className="dgsft-header-text">{col.DISPLAYVALUE}</span>
                            {headerIndicator(col.FIELDNAME)}
                        </button>
                        ))}
                    </div>
                </div>

                <div className="dgsft-tbody" role="rowgroup">
                {displayedRows.length === 0 ? (
                    <div className="dgsft-row dgsft-empty" role="row">
                        <div className="dgsft-cell" role="cell" style={{ gridColumn: `1 / ${columns.length + 1}` }}>
                            No data
                        </div>
                    </div>
                ) : (
                    displayedRows.map((row, rIdx) => (
                    <div className="dgsft-row" role="row" key={rIdx}>
                        {columns.map((col) => (
                        <div className="dgsft-cell" role="cell" key={col.FIELDNAME}>
                            {formatForDisplay(col, row[col.FIELDNAME])}
                        </div>
                        ))}
                    </div>
                    ))
                )}
                </div>
            </div>
        </div>
    </div>
  );
}

/* ======================= Sorting helpers ======================= */

function makeComparator({ sequence, directions }, columns) {
  const colMap = Object.fromEntries(columns.map((c) => [c.FIELDNAME, c]));
  // IMPORTANT: apply from LAST → FIRST (newest → oldest), so last click is highest priority
  const accessors = sequence
    .slice()
    .reverse()
    .map((field) => {
      const col = colMap[field] || { FIELDTYPE: "string" };
      const cmp = getComparatorForType(col);
      const dir = directions[field];
      return { field, cmp, dir };
    });

  return (a, b) => {
    for (const { field, cmp, dir } of accessors) {
      const res = cmp(a[field], b[field]);
      if (res !== 0) return dir === "desc" ? -res : res;
    }
    return a.__index__ - b.__index__;
  };
}

function multiSort(arr, comparator) {
  const deco = arr.map((x, i) => ({ ...x, __index__: i }));
  deco.sort(comparator);
  return deco.map(({ __index__, ...rest }) => rest);
}

function getComparatorForType(col) {
  switch (col.FIELDTYPE) {
    case "number":
      return cmpNumber;
    case "date":
      return cmpDate;
    case "boolean":
      return cmpBoolean;
    case "ip":
      return cmpIP;
    case "enum":
      return (a, b) => cmpEnum(a, b, col.ENUM_ORDER);
    case "string":
    default:
      return cmpString;
  }
}

const NULL_ORDER = (a, b) => {
  const aU = a == null;
  const bU = b == null;
  if (aU && bU) return 0;
  if (aU) return 1; // nulls last
  if (bU) return -1;
  return null;
};

function cmpString(a, b) {
  const n = NULL_ORDER(a, b);
  if (n !== null) return n;
  const as = String(a).toLocaleLowerCase();
  const bs = String(b).toLocaleLowerCase();
  if (as === bs) return 0;
  return as < bs ? -1 : 1;
}

function cmpNumber(a, b) {
  const n = NULL_ORDER(a, b);
  if (n !== null) return n;
  const an = Number(a);
  const bn = Number(b);
  if (Number.isNaN(an) && Number.isNaN(bn)) return 0;
  if (Number.isNaN(an)) return 1;
  if (Number.isNaN(bn)) return -1;
  return an === bn ? 0 : an < bn ? -1 : 1;
}

function coerceDate(v) {
  if (v instanceof Date) return isNaN(v.getTime()) ? null : v;
  if (typeof v === "number") {
    const d = new Date(v);
    return isNaN(d.getTime()) ? null : d;
  }
  if (typeof v === "string") {
    const t = Date.parse(v);
    return isNaN(t) ? null : new Date(t);
  }
  return null;
}

function cmpDate(a, b) {
  const n = NULL_ORDER(a, b);
  if (n !== null) return n;
  const ad = coerceDate(a);
  const bd = coerceDate(b);
  if (!ad && !bd) return 0;
  if (!ad) return 1;
  if (!bd) return -1;
  const diff = ad.getTime() - bd.getTime();
  return diff === 0 ? 0 : diff < 0 ? -1 : 1;
}

function cmpBoolean(a, b) {
  const n = NULL_ORDER(a, b);
  if (n !== null) return n;
  const av = a === true || a === "true" || a === 1 || a === "1";
  const bv = b === true || b === "true" || b === 1 || b === "1";
  if (av === bv) return 0;
  return av ? -1 : 1; // true before false
}

function ipToInt(ip) {
  if (typeof ip !== "string") return null;
  const parts = ip.split(".");
  if (parts.length !== 4) return null;
  const nums = parts.map((p) => {
    const n = Number(p);
    return Number.isInteger(n) && n >= 0 && n <= 255 ? n : NaN;
  });
  if (nums.some((n) => Number.isNaN(n))) return null;
  return ((nums[0] << 24) >>> 0) + (nums[1] << 16) + (nums[2] << 8) + nums[3];
}

function cmpIP(a, b) {
  const n = NULL_ORDER(a, b);
  if (n !== null) return n;
  const ai = ipToInt(String(a));
  const bi = ipToInt(String(b));
  if (ai == null && bi == null) return 0;
  if (ai == null) return 1;
  if (bi == null) return -1;
  return ai === bi ? 0 : ai < bi ? -1 : 1;
}

function cmpEnum(a, b, order = []) {
  const n = NULL_ORDER(a, b);
  if (n !== null) return n;
  const idx = (v) => {
    const s = String(v).toLocaleLowerCase();
    const i = order.findIndex((x) => String(x).toLocaleLowerCase() === s);
    return i === -1 ? Number.POSITIVE_INFINITY : i;
  };
  const ai = idx(a);
  const bi = idx(b);
  if (ai === bi) return cmpString(a, b);
  return ai < bi ? -1 : 1;
}

/* ======================= Minimal styles ======================= */

const styles = `
.dgsft-wrap { width: 100%; overflow: auto; font-family: system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial; }
.dgsft-table { min-width: 720px; border: 1px solid #e5e7eb; border-radius: 12px; overflow: hidden; }
.dgsft-thead, .dgsft-tbody { display: block; }
.dgsft-row { display: grid; grid-template-columns: var(--dgsft-columns, auto); }
.dgsft-header { position: sticky; top: 0; z-index: 1; background: #f8fafc; border-bottom: 1px solid #e5e7eb; }
.dgsft-cell { padding: 10px 12px; border-bottom: 1px solid #f1f5f9; font-size: 14px; line-height: 1.35; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.dgsft-th { font-weight: 600; text-align: left; display: flex; align-items: center; gap: 8px; border: none; background: transparent; cursor: pointer; padding: 12px 12px; transition: background 120ms ease; }
.dgsft-th:hover { background: #eef2ff; }
.dgsft-th.is-sorted { background: #eef2ff; }
.dgsft-header-text { flex: 0 1 auto; }
.dgsft-header-indicator { display: inline-flex; align-items: center; gap: 6px; font-size: 12px; opacity: 0.8; }
.dgsft-badge { display: inline-flex; align-items: center; justify-content: center; min-width: 16px; height: 16px; padding: 0 4px; border-radius: 999px; background: #111827; color: #fff; font-size: 10px; line-height: 1; }
.dgsft-empty .dgsft-cell { text-align: center; color: #6b7280; }
`;
